import {FieldNames} from '../../../../fields'
import {findByFieldName} from '../../../../fields/utils/fieldNames.utils'
import {NOT_EDITABLE_FIELD} from '../consts'
import {ChangeableField} from '../types'

import {ChangeableFieldsMode, useChangeableFields} from './useChangeableFields'

/**
 * Hook to get changeable field state for a single field.
 * @param fieldName - The field name to get state for
 * @param mode - Which level of rules to use (default: 'final')
 * @returns The changeable field state
 */
export function useChangeableField(
  fieldName: FieldNames,
  mode?: ChangeableFieldsMode
): ChangeableField

/**
 * Hook to get changeable field states for multiple fields.
 * Returns an array with the same length as the input array.
 * @param fieldNames - Array of field names to get states for
 * @param mode - Which level of rules to use (default: 'final')
 * @returns Array of changeable field states matching the input array
 */
export function useChangeableField(
  fieldNames: readonly FieldNames[],
  mode?: ChangeableFieldsMode
): ChangeableField[]

/**
 * Implementation of useChangeableField that handles both single and multiple field names.
 */
export function useChangeableField(
  fieldOrFields: FieldNames | readonly FieldNames[],
  mode: ChangeableFieldsMode = 'final'
): ChangeableField | ChangeableField[] {
  const {changeableFields} = useChangeableFields(mode)

  const getFieldState = (name: FieldNames): ChangeableField =>
    findByFieldName(changeableFields, name) ?? NOT_EDITABLE_FIELD

  return Array.isArray(fieldOrFields)
    ? fieldOrFields.map(getFieldState)
    : getFieldState(fieldOrFields as FieldNames)
}
